import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from 'tailwind-v4/components/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'tailwind-v4/components/dialog'
import { Spinner } from 'tailwind-v4/components/spinner'
import { Textarea } from 'tailwind-v4/components/textarea'
import { cn } from 'tailwind-v4/lib/utils'
import trunkApi from '@/state/trunk/trunk.slice'

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const textToHtml = (text: string) =>
  text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('')

/**
 * "Suggest a reply" in the composer toolbar (zion A3: the Coordinator
 * proposes, a person decides). Asks trunk for the next reply given the whole
 * file and the thread, shows it with the reasoning, lets the person edit,
 * then inserts it into the editor. Nothing is sent from here.
 */
export function SuggestReplyButton({ orgId, enquiryId, onInsert, className }: { orgId: string; enquiryId: string; onInsert: (html: string) => void; className?: string }) {
  const [suggest, { isLoading }] = trunkApi.useSuggestForEnquiryMutation()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [rationale, setRationale] = useState('')
  const ask = async () => {
    try {
      const r = await suggest({ orgId, enquiryId, kind: 'reply' }).unwrap()
      if (r.kind !== 'reply' || !r.body_text) {
        toast.error('The Coordinator had nothing to suggest for this thread.')
        return
      }
      setDraft(r.body_text)
      setRationale(r.rationale)
      setOpen(true)
    } catch {
      toast.error('The Coordinator could not draft a reply just now.')
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={() => void ask()}
        disabled={isLoading}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
          'text-muted-foreground hover:bg-foreground/5 hover:text-foreground disabled:opacity-60',
          className
        )}
      >
        {isLoading ? <Spinner className="size-3" /> : <Sparkles className="size-3" />}
        Suggest a reply
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="new-ui-theme sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>A reply to work from</DialogTitle>
            <DialogDescription>{rationale || 'Drafted from the file and the conversation so far. Edit it, then insert.'}</DialogDescription>
          </DialogHeader>
          <Textarea value={draft} rows={10} onChange={(e) => setDraft(e.target.value)} className="text-sm leading-6" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Discard
            </Button>
            <Button
              variant="primary"
              disabled={!draft.trim()}
              onClick={() => {
                onInsert(textToHtml(draft))
                setOpen(false)
              }}
            >
              Insert into the message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
